import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { api, media } from "@/lib/api";
import { useMeta } from "@/lib/useMeta";
import { Rich, Reveal } from "@/components/site/Rich";
import { Carousel } from "@/components/site/Carousel";
import { PdfViewer } from "@/components/site/PdfViewer";

const BG = { white: "bg-white", ivory: "bg-ivory", alt: "bg-ivory-alt", charcoal: "bg-charcoal" };

const Btn = ({ text, url, testid }) => {
  if (!text) return null;
  const cls = "inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white font-bold text-sm rounded-full px-6 py-3 transition-colors";
  const inner = <>{text}<ArrowRight className="w-4 h-4" /></>;
  return url?.startsWith("/") ? <Link to={url} className={cls} data-testid={testid}>{inner}</Link>
    : <a href={url || "#"} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>{inner}</a>;
};

const Heading = ({ text, dark, center }) => !text ? null : (
  <>
    <span className={`block h-[2px] w-12 bg-brass mb-5 ${center ? "mx-auto" : ""}`} />
    <h2 className={`text-3xl sm:text-4xl mb-6 ${dark ? "text-white" : "text-charcoal"} ${center ? "text-center" : ""}`}>{text}</h2>
  </>
);

const textCls = (dark) => (dark ? "text-stone-300" : "text-stone-700");

const Hero = ({ d, i }) => (
  <section className="relative bg-charcoal overflow-hidden">
    {d.image && <img src={media(d.image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />}
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
      <span className="block h-[2px] w-12 bg-brass mb-5" />
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white break-words" data-testid={`cp-hero-title-${i}`}>{d.title}</h1>
      {d.subtitle && <p className="mt-4 text-stone-200 text-base sm:text-lg max-w-2xl">{d.subtitle}</p>}
      {d.button_text && <div className="mt-8"><Btn text={d.button_text} url={d.button_url} testid={`cp-btn-${i}`} /></div>}
    </div>
  </section>
);

const Text = ({ d, dark }) => (
  <Reveal className="max-w-4xl mx-auto px-4 sm:px-6">
    <Heading text={d.title} dark={dark} />
    <Rich html={d.body_html} className={`${textCls(dark)} leading-relaxed text-sm sm:text-base`} />
  </Reveal>
);

const ImageText = ({ d, dark, i }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
    <Reveal className={`min-w-0 ${d.image_side === "left" ? "lg:order-2" : ""}`}>
      <Heading text={d.title} dark={dark} />
      <Rich html={d.body_html} className={`${textCls(dark)} leading-relaxed text-sm sm:text-base`} />
      {d.button_text && <div className="mt-7"><Btn text={d.button_text} url={d.button_url} testid={`cp-btn-${i}`} /></div>}
    </Reveal>
    {d.image && <Reveal delay={0.1}><img src={media(d.image)} alt="" className="w-full h-auto rounded-md shadow-xl" /></Reveal>}
  </div>
);

const Image = ({ d, dark }) => !d.image ? null : (
  <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
    <img src={media(d.image)} alt={d.caption || ""} className="block max-w-full h-auto mx-auto shadow-lg" style={{ width: `${Number(d.image_width) || 100}%` }} />
    {d.caption && <p className={`mt-3 text-sm ${dark ? "text-stone-400" : "text-stone-500"}`}>{d.caption}</p>}
  </Reveal>
);

const Card = ({ c, i, j }) => (
  <div className="bg-white h-full flex flex-col rounded-md overflow-hidden shadow-sm border border-stone-100" data-testid={`cp-card-${i}-${j}`}>
    {c.image && <img src={media(c.image)} alt="" className="w-full h-44 object-cover" />}
    <div className="p-7 flex flex-col flex-1">
      <span className="block h-[2px] w-10 bg-brass mb-4" />
      <h3 className="text-2xl text-crimson mb-3">{c.title}</h3>
      <p className="text-sm text-stone-600 leading-relaxed flex-1 whitespace-pre-line">{c.body}</p>
      {c.link_text && (c.link_url?.startsWith("/")
        ? <Link to={c.link_url} className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-crimson">{c.link_text}<ArrowRight className="w-4 h-4" /></Link>
        : <a href={c.link_url || "#"} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-crimson">{c.link_text}<ArrowRight className="w-4 h-4" /></a>)}
    </div>
  </div>
);

const Cards = ({ d, dark, i }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6">
    <Heading text={d.title} dark={dark} />
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {(d.items || []).map((c, j) => <Reveal key={j} delay={j * 0.08} className="h-full"><Card c={c} i={i} j={j} /></Reveal>)}
    </div>
  </div>
);

const Gallery = ({ d, dark, i }) => (
  <>
    {d.title && <h2 className={`text-2xl sm:text-3xl lg:text-4xl text-center mb-12 px-4 ${dark ? "text-white" : "text-charcoal"}`}>{d.title}</h2>}
    <Carousel images={d.images || []} testid={`cp-carousel-${i}`} />
  </>
);

const Cta = ({ d, dark, i }) => (
  <Reveal className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
    {d.title && <h2 className={`text-2xl sm:text-3xl ${dark ? "text-white" : "text-charcoal"}`}>{d.title}</h2>}
    <Rich html={d.body_html} className={`mt-4 ${textCls(dark)} [&_a]:text-brass`} />
    {d.button_text && <div className="mt-8"><Btn text={d.button_text} url={d.button_url} testid={`cp-btn-${i}`} /></div>}
  </Reveal>
);

const Embed = ({ d, dark }) => !d.url ? null : (
  <div className="max-w-5xl mx-auto px-2 sm:px-6">
    <Heading text={d.title} dark={dark} center />
    <iframe src={d.url} title={d.title || "Embedded content"} className="w-full border-0 bg-white" style={{ height: Number(d.height) || 800 }} allowFullScreen />
  </div>
);

const Pdf = ({ d, i }) => !d.pdf ? null : (
  <div>
    <PdfViewer url={media(d.pdf)} />
    <div className="flex justify-center pt-10">
      <a href={`${media(d.pdf)}?download=1`} download className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white font-bold text-sm rounded-full px-7 py-3.5" data-testid={`cp-pdf-download-${i}`}>
        <Download className="w-4 h-4" />{d.button_text || "Download PDF"}
      </a>
    </div>
  </div>
);

const RENDER = { text: Text, image_text: ImageText, image: Image, cards: Cards, gallery: Gallery, cta: Cta, embed: Embed, pdf: Pdf };

export const PageSections = ({ sections }) => sections.map((s, i) => {
  if (s.type === "hero") return <Hero key={s.id} d={s.data} i={i} />;
  const C = RENDER[s.type];
  if (!C) return null;
  const dark = s.data.background === "charcoal";
  return (
    <section key={s.id} className={`${BG[s.data.background] || "bg-white"} ${s.type === "pdf" ? "pb-14" : "py-16 sm:py-20"}`} data-testid={`cp-section-${i}`}>
      <C d={s.data} dark={dark} i={i} />
    </section>
  );
});

export default function CustomPage({ fallback }) {
  const { slug } = useParams();
  const [page, setPage] = useState(undefined);

  useEffect(() => {
    setPage(undefined);
    api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setPage(null));
  }, [slug]);

  useMeta(null, {}, page ? { title: page.meta_title || `${page.title} | Indian Hotel Directory`, description: page.meta_description } : null);

  if (page === undefined) return <div className="py-24 text-center text-stone-400" data-testid="custom-page-loading">Loading…</div>;
  if (!page) return fallback;
  return (
    <div data-testid="custom-page">
      {page.status !== "published" && <div className="bg-amber-100 text-amber-900 text-center text-sm py-2 font-sans" data-testid="custom-page-draft-banner">Draft preview – only visible to you while logged in as admin</div>}
      {!page.sections.some((s) => s.type === "hero") && <h1 className="sr-only">{page.title}</h1>}
      <PageSections sections={page.sections} />
    </div>
  );
}
